import { COUNTRIES } from "@/components/UI/forms/CountrySelect";
import { SUBDIVISIONS } from "@/components/UI/forms/SubdivisionSelect";

// Application status type from backend
export type ApplicationStatus = "pending" | "approved" | "rejected";

// Create a lookup map from ISO code to country name
export const COUNTRY_MAP = COUNTRIES.reduce(
  (acc, c) => {
    acc[c.iso] = c.country;
    return acc;
  },
  {} as Record<string, string>,
);

// Helper to get country name from ISO code
export const getCountryName = (isoCode: string | null): string => {
  if (!isoCode) return "—";
  return COUNTRY_MAP[isoCode] || isoCode;
};

// Lookup from ISO 3166-2 code (e.g. "US-CA") to subdivision name. The admin UI
// is English-only, so the German names are ignored here.
export const SUBDIVISION_MAP = Object.values(SUBDIVISIONS)
  .flat()
  .reduce(
    (acc, s) => {
      acc[s.code] = s.name;
      return acc;
    },
    {} as Record<string, string>,
  );

// Helper to get subdivision name from ISO 3166-2 code
export const getSubdivisionName = (code: string | null): string => {
  if (!code) return "—";
  return SUBDIVISION_MAP[code] || code;
};

// Helper to format status with color
export const formatStatus = (
  status: ApplicationStatus,
): { text: string; color: string; colorScheme: string } => {
  switch (status) {
    case "pending":
      return { text: "Pending", color: "yellow.600", colorScheme: "yellow" };
    case "approved":
      return { text: "Approved", color: "green.600", colorScheme: "green" };
    case "rejected":
      return { text: "Rejected", color: "red.600", colorScheme: "red" };
    default:
      return { text: status, color: "gray.500", colorScheme: "gray" };
  }
};

// Helper to calculate age from date of birth
export const calculateAge = (dateOfBirth: string | null, includeUnit: boolean = false): string => {
  if (!dateOfBirth) return "—";
  const birthDate = new Date(dateOfBirth);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return includeUnit ? `${age} years` : `${age}`;
};

// Helper to format boolean
export const formatBoolean = (value: boolean | null): string => {
  if (value === null) return "—";
  return value ? "Yes" : "No";
};

// Helper to format application type
export const formatApplicationType = (type: string | null): string => {
  if (!type) return "—";
  return type === "self" ? "Self" : "Proxy";
};

// Tagged lifecycle. Centralizes the (status, started_at) → render-state
// derivation so the column / detail page / banner all read the same thing.
// The response shape allows combinations the business logic forbids
// (e.g. status="approved" with started_at=null); a malformed pair collapses
// to "none" rather than rendering a half-broken row.
export type ApplicationLifecycle =
  | { kind: "none" }
  | { kind: "draft"; startedAt: string }
  | { kind: "pending"; startedAt: string; lastReviewAt: string | null }
  | { kind: "approved"; startedAt: string; lastReviewAt: string | null }
  | { kind: "rejected"; startedAt: string; lastReviewAt: string | null };

interface LifecycleInput {
  application_status: "none" | "pending" | "approved" | "rejected";
  application_started_at: string | null;
  last_review_at: string | null;
}

export const lifecycleOf = (input: LifecycleInput): ApplicationLifecycle => {
  if (input.application_status === "none") {
    return input.application_started_at
      ? { kind: "draft", startedAt: input.application_started_at }
      : { kind: "none" };
  }
  if (!input.application_started_at) {
    console.error("Account has non-'none' status without started_at", input);
    return { kind: "none" };
  }
  return {
    kind: input.application_status,
    startedAt: input.application_started_at,
    lastReviewAt: input.last_review_at,
  };
};
